import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Select, Input, Button, Row, Col, Alert, Space } from 'antd'
import {
  getRequest,
  patchRequest,
  carMakesUrl,
  customDutyBodyTypesUrl,
  fuelTypesUrl,
  driveTrainsUrl,
  originsUrl,
  conditionsUrl,
  dutyCarRequestsUrl,
  successNotification,
  errorNotification
} from '../../../config'

const { Option } = Select

// Dropdown (enum) fields. The backend resolves a car's slot by NAME, so each option's value
// is the record's name and that name is what we send — no id↔name mapping, and the current
// slot value (also a name) prefills directly. `src` keys the loaded option list below.
const SELECT_FIELDS = [
  { slot: 'make', key: 'make', label: 'Make', src: 'makes' },
  { slot: 'body_type', key: 'bodyType', label: 'Body Type', src: 'bodyTypes' },
  { slot: 'fuel_type', key: 'fuelType', label: 'Fuel Type', src: 'fuelTypes' },
  { slot: 'origin', key: 'origin', label: 'Origin', src: 'origins' },
  { slot: 'drive_train', key: 'driveTrain', label: 'Drive Train', src: 'driveTrains' },
  { slot: 'condition', key: 'condition', label: 'Condition', src: 'conditions' }
]

// Free-text fields. Model is text because the backend creates it scoped to the chosen make.
const TEXT_FIELDS = [
  { slot: 'model', key: 'model', label: 'Model' },
  { slot: 'engine_size', key: 'engineSize', label: 'Engine Size (Litres)' },
  { slot: 'trim', key: 'trim', label: 'Trim' },
  { slot: 'chassis_number', key: 'chassisNumber', label: 'Chassis / VIN' },
  { slot: 'passengers', key: 'passengers', label: 'Passengers' }
]

const ALL_KEYS = [
  ...SELECT_FIELDS.map(f => f.key),
  ...TEXT_FIELDS.map(f => f.key),
  'carYear',
  'isSpecialPurpose'
]

const getYears = () => {
  const years = []
  for (let i = new Date().getFullYear() + 1; i >= 1960; i--) {
    years.push(i)
  }
  return years
}

const truthy = v => [true, 'true', 'yes', 'y', '1', 1].includes(v)

// Currencies support commonly enters an MSRP in. USD is the default the pipeline normalises from.
const MSRP_CURRENCIES = ['USD', 'GHS', 'EUR', 'GBP', 'CAD', 'JPY', 'AED']

const CarEditForm = ({ car, onDone }) => {
  const slots = car.slots || {}
  // The API's camelCase renderer also camelCases the nested slot keys, so `car_year` arrives as
  // `carYear` (etc.). Read defensively by trying both the snake_case name and its camelCase form
  // so existing collected values pre-fill the form correctly.
  const toCamel = s => s.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase())
  const slotEntry = slot => slots[slot] || slots[toCamel(slot)] || null
  const slotVal = slot => {
    const entry = slotEntry(slot)
    return entry && entry.value !== undefined && entry.value !== null
      ? entry.value
      : undefined
  }

  const [values, setValues] = useState({
    make: slotVal('make'),
    model: slotVal('model'),
    carYear: slotVal('car_year') ? Number(slotVal('car_year')) : undefined,
    bodyType: slotVal('body_type'),
    fuelType: slotVal('fuel_type'),
    origin: slotVal('origin'),
    driveTrain: slotVal('drive_train'),
    condition: slotVal('condition'),
    engineSize: slotVal('engine_size'),
    trim: slotVal('trim'),
    chassisNumber: slotVal('chassis_number'),
    passengers: slotVal('passengers'),
    isSpecialPurpose:
      slotVal('is_special_purpose') === undefined
        ? undefined
        : truthy(slotVal('is_special_purpose'))
  })

  // Manual MSRP override (separate from slots): prefills from any override already set. The
  // finalized record's current MSRP is shown as a hint so support knows what the estimate used.
  const [manualMsrp, setManualMsrp] = useState(
    car.manualMsrp !== undefined && car.manualMsrp !== null ? String(car.manualMsrp) : ''
  )
  const [manualMsrpCurrency, setManualMsrpCurrency] = useState(
    car.manualMsrpCurrency || 'USD'
  )

  const [options, setOptions] = useState({
    makes: [],
    bodyTypes: [],
    fuelTypes: [],
    driveTrains: [],
    origins: [],
    conditions: []
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [unresolved, setUnresolved] = useState([])

  const set = key => value => setValues(v => ({ ...v, [key]: value }))

  useEffect(() => {
    // Each option's value is the record name (what the backend resolves against).
    const load = async (url, src) => {
      const res = await getRequest(`${url}/all`, src, setLoading)
      if (res.success) {
        const list = res.data.map(it => {
          const name = it.name || it.country || it.country_name
          return { value: name, label: name }
        })
        setOptions(o => ({ ...o, [src]: list }))
      }
    }
    load(carMakesUrl, 'makes')
    load(customDutyBodyTypesUrl, 'bodyTypes')
    load(fuelTypesUrl, 'fuelTypes')
    load(driveTrainsUrl, 'driveTrains')
    load(originsUrl, 'origins')
    load(conditionsUrl, 'conditions')
  }, [])

  const handleSave = async () => {
    const slotValues = {}
    ALL_KEYS.forEach(k => {
      const v = values[k]
      if (v !== undefined && v !== null && v !== '') slotValues[k] = v
    })
    const body = { slotValues }
    // Send the MSRP override alongside the slots. An empty value clears any existing override
    // (the finalizer falls back to the AI MSRP). Non-empty must be numeric.
    const msrp = (manualMsrp || '').trim()
    if (msrp === '') {
      body.manualMsrp = ''
    } else if (isNaN(Number(msrp))) {
      errorNotification('MSRP must be a number (or leave it blank to use the AI MSRP).')
      return
    } else {
      body.manualMsrp = msrp
      body.manualMsrpCurrency = manualMsrpCurrency || 'USD'
    }
    const res = await patchRequest(
      `${dutyCarRequestsUrl}/${car.id}/`,
      body,
      'car details',
      setSaving
    )
    if (res.success) {
      const stillUnresolved = res.data.unresolved || []
      setUnresolved(stillUnresolved)
      if (stillUnresolved.length) {
        errorNotification(
          `Saved, but these still need a valid value: ${stillUnresolved.join(', ')}`
        )
      } else {
        successNotification('Car details updated')
      }
      onDone && onDone()
    }
  }

  return (
    <div>
      {unresolved.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Some details could not be matched"
          description={`Fix these so the car can be processed: ${unresolved.join(
            ', '
          )}`}
        />
      ) : null}

      <Row gutter={[12, 12]}>
        {SELECT_FIELDS.map(f => (
          <Col xs={24} md={12} key={f.key}>
            <label style={{ display: 'block', marginBottom: 4 }}>{f.label}</label>
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder={`Select ${f.label.toLowerCase()}`}
              style={{ width: '100%' }}
              loading={loading}
              value={values[f.key]}
              onChange={set(f.key)}
            >
              {options[f.src].map(o => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Col>
        ))}

        <Col xs={24} md={12}>
          <label style={{ display: 'block', marginBottom: 4 }}>Year</label>
          <Select
            showSearch
            allowClear
            placeholder="Select year"
            style={{ width: '100%' }}
            value={values.carYear}
            onChange={set('carYear')}
          >
            {getYears().map(y => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </Select>
        </Col>

        {TEXT_FIELDS.map(f => (
          <Col xs={24} md={12} key={f.key}>
            <label style={{ display: 'block', marginBottom: 4 }}>{f.label}</label>
            <Input
              placeholder={f.label}
              value={values[f.key]}
              onChange={e => set(f.key)(e.target.value)}
            />
          </Col>
        ))}

        <Col xs={24} md={12}>
          <label style={{ display: 'block', marginBottom: 4 }}>
            Special Purpose
          </label>
          <Select
            allowClear
            placeholder="Special purpose?"
            style={{ width: '100%' }}
            value={values.isSpecialPurpose}
            onChange={set('isSpecialPurpose')}
          >
            <Option value={true}>Yes</Option>
            <Option value={false}>No</Option>
          </Select>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16, marginBottom: 8 }}
        message="Manual MSRP override"
        description={
          <span>
            Set this when the car&apos;s HDV/MSRP doesn&apos;t match the value local customs
            apply. It replaces the AI MSRP on the next finalize / re-finalize. Leave blank to
            use the AI MSRP.
            {car.carDetailsMsrp
              ? ` Current estimate used: ${car.carDetailsMsrp} ${
                  car.carDetailsMsrpCurrency || ''
                }.`
              : ''}
          </span>
        }
      />
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <label style={{ display: 'block', marginBottom: 4 }}>MSRP Amount</label>
          <Input
            placeholder="e.g. 18500 (blank = use AI MSRP)"
            value={manualMsrp}
            onChange={e => setManualMsrp(e.target.value)}
          />
        </Col>
        <Col xs={24} md={12}>
          <label style={{ display: 'block', marginBottom: 4 }}>MSRP Currency</label>
          <Select
            style={{ width: '100%' }}
            value={manualMsrpCurrency}
            onChange={setManualMsrpCurrency}
          >
            {MSRP_CURRENCIES.map(c => (
              <Option key={c} value={c}>
                {c}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save Details
          </Button>
        </Space>
      </div>
    </div>
  )
}

CarEditForm.propTypes = {
  car: PropTypes.object,
  onDone: PropTypes.func
}

export default CarEditForm
